import { Alert, Button, Group, NumberInput, Select, Stack, Text, TextInput, Textarea } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useQuery } from '@tanstack/react-query'
import {
  ETIQUETA_FICHA, TIPOS_FICHA, TIPOS_SUMINISTRO, fichaEntrada,
  type CriterioFicha, type Empresa, type Ficha, type FichaEntrada,
  type Proveedor, type TipoFicha,
} from '@serkat/shared'
import { api } from '../../../api/cliente'
import { hoyLocal } from '../../../compartido/formato'
import { TablaCriterios } from './TablaCriterios'

interface Props {
  inicial?: Ficha
  tipoInicial: TipoFicha
  guardando: boolean
  onGuardar: (datos: FichaEntrada) => void
  onCancelar: () => void
}

export function FormularioFicha({ inicial, tipoInicial, guardando, onGuardar, onCancelar }: Props) {
  const form = useForm<FichaEntrada>({
    initialValues: {
      tipo: inicial?.tipo ?? tipoInicial,
      empresaId: inicial?.empresaId ?? 0,
      correlativo: inicial?.correlativo ?? 0,
      proveedorId: inicial?.proveedorId ?? '',
      fechaEmision: inicial?.fechaEmision ?? hoyLocal(),
      tipoSuministro: inicial?.tipoSuministro ?? 'PRODUCTO',
      ordenId: inicial?.ordenId ?? null,
      observaciones: inicial?.observaciones ?? null,
      calificaciones: inicial?.calificaciones.map((c) => ({
        criterioId: c.criterioId, calificacion: c.calificacion, observaciones: c.observaciones,
      })) ?? [],
    },
    validate: zodResolver(fichaEntrada),
  })

  const tipo = form.values.tipo

  const { data: criterios } = useQuery({
    queryKey: ['fichas', 'criterios', tipo],
    queryFn: () => api.get<CriterioFicha[]>(`/fichas/criterios?tipo=${tipo}`),
    staleTime: 3_600_000,
  })

  const { data: empresas } = useQuery({
    queryKey: ['empresas', 'activas'],
    queryFn: () => api.get<Empresa[]>('/empresas'),
  })

  const { data: proveedores } = useQuery({
    queryKey: ['proveedores', 'todos'],
    queryFn: () => api.get<{ datos: Proveedor[] }>('/proveedores?porPagina=100&activo=true'),
  })

  const { data: propuesto } = useQuery({
    queryKey: ['fichas', 'siguiente-numero', tipo],
    queryFn: () => api.get<{ serie: string; correlativo: number }>(`/fichas/siguiente-numero?tipo=${tipo}`),
    enabled: !inicial,
    staleTime: 0,
  })

  if (!inicial) {
    if (propuesto && form.values.correlativo === 0) {
      form.setFieldValue('correlativo', propuesto.correlativo)
    }
  }

  const numero = form.values.correlativo && propuesto
    ? `${propuesto.serie}-${String(form.values.correlativo).padStart(3, '0')}`
    : inicial?.numero ?? '—'

  function cambiarTipo(v: string | null) {
    const t = (v ?? 'SELECCION_PROVEEDOR') as TipoFicha
    form.setFieldValue('tipo', t)
    // Cada tipo tiene sus propios criterios y su propio libro de correlativos.
    form.setFieldValue('calificaciones', [])
    form.setFieldValue('correlativo', 0)
    form.setFieldValue('ordenId', null)
  }

  return (
    <form onSubmit={form.onSubmit(onGuardar)}>
      <Stack gap="sm">
        <Group grow align="flex-start">
          <Select
            label="Tipo de ficha" withAsterisk disabled={Boolean(inicial)}
            data={TIPOS_FICHA.map((t) => ({ value: t, label: ETIQUETA_FICHA[t] }))}
            value={tipo} onChange={cambiarTipo}
          />
          <Select
            label="Empresa emisora" withAsterisk
            placeholder="Elija la empresa que emite"
            description="Se elige en cada documento; no hay una predeterminada" 
            data={(empresas ?? []).map((e) => ({ value: String(e.id), label: e.razonSocial }))}
            value={form.values.empresaId ? String(form.values.empresaId) : null}
            onChange={(v) => form.setFieldValue('empresaId', Number(v))}
            error={form.errors['empresaId']}
          />
          <NumberInput label="Correlativo" description={`Número: ${numero}`} withAsterisk
            min={1} hideControls {...form.getInputProps('correlativo')} />
        </Group>

        <Group grow align="flex-start">
          <Select
            label="Proveedor" withAsterisk searchable disabled={Boolean(inicial)}
            data={(proveedores?.datos ?? []).map((p) => ({
              value: p.id, label: `${p.razonSocial} — ${p.ruc}`,
            }))}
            value={form.values.proveedorId || null}
            onChange={(v) => form.setFieldValue('proveedorId', v ?? '')}
            error={form.errors['proveedorId']}
          />
          <Select
            label="Provee" withAsterisk
            data={TIPOS_SUMINISTRO.map((t) => ({
              value: t, label: t === 'AMBOS' ? 'Producto y servicio' : t.toLowerCase(),
            }))}
            value={form.values.tipoSuministro}
            onChange={(v) => form.setFieldValue('tipoSuministro', (v ?? 'PRODUCTO') as never)}
          />
          <TextInput
            label={tipo === 'SELECCION_PROVEEDOR' ? 'Fecha de selección' : 'Fecha de evaluación'}
            type="date" withAsterisk {...form.getInputProps('fechaEmision')}
          />
        </Group>

        {tipo === 'EVALUACION_PROVEEDOR' && (
          <Alert color="blue" variant="light">
            La periodicidad de la próxima evaluación la fija el sistema según el
            resultado: anual si aprueba, seis meses en los demás casos.
          </Alert>
        )}

        <TablaCriterios
          criterios={criterios ?? []}
          valores={form.values.calificaciones}
          error={form.errors['calificaciones'] as string | undefined}
          onCambiar={(v) => form.setFieldValue('calificaciones', v)}
        />

        <Textarea label="Observaciones" autosize minRows={2}
          value={form.values.observaciones ?? ''}
          onChange={(e) => form.setFieldValue('observaciones', e.currentTarget.value || null)} />

        <Group justify="space-between" mt="xs">
          <Text size="xs" c="dimmed">
            Se emite en estado Pendiente. El puntaje y la condición los calcula el sistema.
          </Text>
          <Group>
            <Button variant="default" onClick={onCancelar}>Cancelar</Button>
            <Button type="submit" loading={guardando}>Guardar</Button>
          </Group>
        </Group>
      </Stack>
    </form>
  )
}
